/**
 * SavedPlays Component
 * Allows premium users to save and load offensive plays.
 *
 * Saving is two flows, not one. Fresh work is named and created. Work that came from a load is
 * assumed to be an EDIT of that play, so saving offers to update it — see `useSaveTarget` for why
 * creating a duplicate silently is the wrong default.
 */

import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import { usePremiumStatus } from '../hooks';
import { throwOnErrors } from '../utils';
import { getRouteDefinition, getRBRouteDefinition } from '../rules/routeRules';
import Modal from './Modal';
import type { SaveTargetHandle } from '../hooks';
import { RouteType, RBRouteType } from '../types';
import './SavedPlays.css';

const client = generateClient<Schema>();

interface SavedPlay {
  id: string;
  name: string;
  wr1Route: string;
  wr2Route: string;
  wr3Route: string;
  wr4Route: string;
  rbRoute: string;
  owner?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PlayPayload {
  wr1Route: RouteType;
  wr2Route: RouteType;
  wr3Route: RouteType;
  wr4Route: RouteType;
  rbRoute: RBRouteType;
}

interface SavedPlaysProps {
  currentPlay: PlayPayload;
  onLoadPlay: (play: PlayPayload) => void;
  /**
   * Which saved play the working copy came from. Owned by the page rather than this component
   * because SimulationPage renders SavedPlays twice (mobile and desktop layouts); component-local
   * state would let one copy think a play is loaded while the other did not.
   */
  saveTarget: SaveTargetHandle<PlayPayload>;
  disabled?: boolean;
}

const toPayload = (play: SavedPlay): PlayPayload => ({
  wr1Route: play.wr1Route as RouteType,
  wr2Route: play.wr2Route as RouteType,
  wr3Route: play.wr3Route as RouteType,
  wr4Route: play.wr4Route as RouteType,
  rbRoute: play.rbRoute as RBRouteType,
});

/**
 * A route's display name, e.g. `deepIn` -> `Dig`.
 *
 * Every route was previously printed as its raw id, so the save dialog and the saved-play list read
 * "WR1: deepIn" and "slant / go / curl / out / swing" — internal identifiers shown to a coach. Falls
 * back to the id only if a route is missing from the registry entirely.
 */
const wrRouteName = (route: string) => getRouteDefinition(route)?.name ?? route;
const rbRouteName = (route: string) => getRBRouteDefinition(route)?.name ?? route;

/** Receiver labels carry the coaching letter, matching RouteControls. */
function PlayPreview({ play }: { play: PlayPayload }) {
  return (
    <div className="play-preview">
      <div className="preview-row">WR1 (X): {wrRouteName(play.wr1Route)}</div>
      <div className="preview-row">WR2 (H): {wrRouteName(play.wr2Route)}</div>
      <div className="preview-row">WR3 (Y): {wrRouteName(play.wr3Route)}</div>
      <div className="preview-row">WR4 (Z): {wrRouteName(play.wr4Route)}</div>
      <div className="preview-row">RB: {rbRouteName(play.rbRoute)}</div>
    </div>
  );
}

function SavedPlays({ currentPlay, onLoadPlay, saveTarget, disabled = false }: SavedPlaysProps) {
  const { isPremium, isAuthenticated, isLoading: authLoading } = usePremiumStatus();
  const { target, isDirty, track, forget } = saveTarget;
  const [plays, setPlays] = useState<SavedPlay[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [playName, setPlayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * Why loading the list failed, if it did.
   *
   * Kept separate from `error` (which belongs to the save dialog) because it has to survive long
   * enough to be shown in the load dialog. It also has to keep the Load button ENABLED — a failed
   * fetch leaves `plays` empty, and disabling on empty would hide the only explanation the user
   * could get.
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  /** 'choose' offers update-vs-new; 'create' is the name-and-save form. */
  const [saveMode, setSaveMode] = useState<'choose' | 'create'>('create');

  // Fetch saved plays when component mounts
  useEffect(() => {
    if (isPremium && isAuthenticated) {
      fetchPlays();
    }
  }, [isPremium, isAuthenticated]);

  const fetchPlays = async () => {
    try {
      setIsLoading(true);
      // Goes through the tier-checked Lambda, not the model directly, so a lapsed subscriber
      // cannot retrieve their plays by calling the API by hand. See docs/premium-gate.md.
      const { data, errors } = await client.queries.fetchSavedPlays();

      // The Amplify client does NOT throw on a GraphQL error — it resolves with `errors` set and
      // `data` null. Ignoring that turned every server-side failure into "you have no saved
      // plays", which renders as a permanently greyed-out Load button with nothing explaining why.
      // Surface it instead.
      throwOnErrors(errors);

      const sortedPlays = ((data || []) as (SavedPlay | null)[])
        .filter((p: SavedPlay | null): p is SavedPlay => p !== null && typeof p.id === 'string')
        .sort((a: SavedPlay, b: SavedPlay) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });
      setPlays(sortedPlays);
      setLoadError(null);
    } catch (err) {
      console.error('Error fetching plays:', err);
      setLoadError(err instanceof Error ? err.message : 'Could not load your saved plays.');
    } finally {
      setIsLoading(false);
    }
  };

  /** Opening the dialog decides which flow to show: edit-of-a-load, or brand new work. */
  const openSaveModal = () => {
    setError(null);
    setPlayName('');
    setSaveMode(target ? 'choose' : 'create');
    setShowSaveModal(true);
  };

  const finishSave = (message: string) => {
    setSaveSuccess(message);
    setTimeout(() => {
      setSaveSuccess(null);
      setShowSaveModal(false);
    }, 1500);
  };

  const handleSave = async () => {
    if (!playName.trim()) {
      setError('Please enter a play name');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data: created, errors } = await client.mutations.savePlay({
        name: playName.trim(),
        ...currentPlay,
      });

      // A GraphQL error resolves rather than throws, so without these two checks the code fell
      // through to finishSave() and told the user "✓ Play saved!" for a play that was never
      // written. Treat a missing record as a failure too — success means we got the play back.
      throwOnErrors(errors);
      if (!created?.id) throw new Error('The play was not saved. Please try again.');

      // The new play becomes the one being worked from, so the NEXT save offers to update it
      // rather than creating a third copy.
      track({ id: created.id, name: created.name, snapshot: { ...currentPlay } });

      setPlayName('');
      await fetchPlays();
      finishSave('✓ Play saved!');
    } catch (err) {
      console.error('Error saving play:', err);
      // Show what actually went wrong. The server's messages here are already user-facing
      // ("A Premium subscription is required…"), and a generic string hid a real failure behind a
      // success message for the whole of this feature's first release.
      setError(err instanceof Error ? err.message : 'Failed to save play');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!target) return;

    try {
      setIsLoading(true);
      setError(null);

      const { errors } = await client.mutations.editSavedPlay({ id: target.id, ...currentPlay });
      throwOnErrors(errors);

      // Re-snapshot so the play reads as clean again; otherwise the dialog would keep insisting
      // there are unsaved changes immediately after saving them.
      track({ ...target, snapshot: { ...currentPlay } });

      await fetchPlays();
      finishSave('✓ Play updated!');
    } catch (err) {
      console.error('Error updating play:', err);
      setError(err instanceof Error ? err.message : 'Failed to update play');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoad = (play: SavedPlay) => {
    const payload = toPayload(play);
    onLoadPlay(payload);
    track({ id: play.id, name: play.name, snapshot: payload });
    setShowLoadModal(false);
  };

  const handleDelete = async (playId: string) => {
    try {
      await client.mutations.removeSavedPlay({ id: playId });
      // Nothing to update anymore — a later save has to create.
      forget(playId);
      await fetchPlays();
    } catch (err) {
      console.error('Error deleting play:', err);
    }
  };

  // Don't show anything while loading auth
  if (authLoading) {
    return null;
  }

  // Non-premium: show the controls so the feature is discoverable, but locked.
  //
  // They are rendered `disabled` rather than merely styled as locked. A disabled button fires no
  // click handler, so there is no path from here to a create/update/delete — the gate is the
  // absence of a reachable code path, not a CSS class. The modals below are not rendered at all.
  if (!isPremium) {
    return (
      <div className="saved-plays saved-plays-locked">
        <div className="saved-plays-buttons">
          <button className="saved-plays-btn save-btn" disabled type="button" title="Premium feature">
            🔒 Save Play
          </button>
          <button className="saved-plays-btn load-btn" disabled type="button" title="Premium feature">
            🔒 Load Play
          </button>
        </div>
        <a className="saved-plays-upgrade-link" href="/pricing">
          Save plays with Premium
        </a>
      </div>
    );
  }

  return (
    <div className="saved-plays">
      <div className="saved-plays-buttons">
        <button
          className="saved-plays-btn save-btn"
          onClick={openSaveModal}
          disabled={disabled}
          type="button"
        >
          💾 Save Play{isDirty ? ' *' : ''}
        </button>
        <button
          className="saved-plays-btn load-btn"
          onClick={() => setShowLoadModal(true)}
          disabled={disabled || (plays.length === 0 && !loadError)}
          type="button"
        >
          📂 Load Play {plays.length > 0 && `(${plays.length})`}
        </button>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <Modal
          title="Save Play"
          onClose={() => setShowSaveModal(false)}
          overlayClassName="saved-plays-modal-overlay"
          panelClassName="saved-plays-modal"
        >
              {saveSuccess ? (
                <div className="save-success">{saveSuccess}</div>
              ) : saveMode === 'choose' && target ? (
                <>
                  <p className="save-choice-prompt">
                    {isDirty ? (
                      <>You've made changes to <strong>{target.name}</strong>.</>
                    ) : (
                      <>You're working from <strong>{target.name}</strong>, with no changes yet.</>
                    )}
                  </p>
                  {error && <div className="modal-error">{error}</div>}
                  <PlayPreview play={currentPlay} />
                  <div className="save-choice-actions">
                    <button
                      className="modal-save-btn"
                      onClick={handleUpdate}
                      disabled={isLoading || !isDirty}
                      title={isDirty ? undefined : 'Nothing has changed since you loaded it'}
                    >
                      {isLoading ? 'Updating...' : `Update "${target.name}"`}
                    </button>
                    <button
                      className="modal-secondary-btn"
                      onClick={() => {
                        setError(null);
                        setPlayName('');
                        setSaveMode('create');
                      }}
                      disabled={isLoading}
                    >
                      Save as a new play
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {target && (
                    <button
                      className="modal-back-btn"
                      onClick={() => {
                        setError(null);
                        setSaveMode('choose');
                      }}
                      disabled={isLoading}
                    >
                      ← Back
                    </button>
                  )}
                  <input
                    type="text"
                    placeholder="Enter play name..."
                    value={playName}
                    onChange={(e) => setPlayName(e.target.value)}
                    className="play-name-input"
                    maxLength={50}
                    autoFocus
                  />
                  {error && <div className="modal-error">{error}</div>}
                  <PlayPreview play={currentPlay} />
                  <button
                    className="modal-save-btn"
                    onClick={handleSave}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save Play'}
                  </button>
                </>
              )}
        </Modal>
      )}

      {/* Load Modal */}
      {showLoadModal && (
        <Modal
          title="Load Play"
          onClose={() => setShowLoadModal(false)}
          overlayClassName="saved-plays-modal-overlay"
          panelClassName="saved-plays-modal"
        >
              {isLoading ? (
                <div className="loading-plays">Loading...</div>
              ) : loadError ? (
                <>
                  <div className="modal-error">{loadError}</div>
                  <button className="modal-secondary-btn" onClick={fetchPlays}>Try again</button>
                </>
              ) : plays.length === 0 ? (
                <div className="no-plays">No saved plays yet</div>
              ) : (
                <ul className="plays-list">
                  {plays.map((play) => (
                    <li key={play.id} className={`play-item${target?.id === play.id ? ' play-item-current' : ''}`}>
                      {/* A real button, not a div with onClick. Loading a play was mouse-only. */}
                      <button
                        className="play-info"
                        type="button"
                        onClick={() => handleLoad(play)}
                      >
                        <span className="play-name">
                          {play.name}
                          {target?.id === play.id && (
                            <span className="play-current-badge">{isDirty ? 'edited' : 'loaded'}</span>
                          )}
                        </span>
                        <span className="play-routes">
                          {wrRouteName(play.wr1Route)} / {wrRouteName(play.wr2Route)} /{' '}
                          {wrRouteName(play.wr3Route)} / {wrRouteName(play.wr4Route)} /{' '}
                          {rbRouteName(play.rbRoute)}
                        </span>
                      </button>
                      <button
                        className="play-delete-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(play.id);
                        }}
                        // The emoji was this control's only content, so it had no accessible name —
                        // `title` is not a reliable one. The name includes the play so a screen
                        // reader user knows WHICH delete they are on.
                        aria-label={`Delete ${play.name}`}
                      >
                        <span aria-hidden="true">🗑️</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
        </Modal>
      )}
    </div>
  );
}

export default SavedPlays;
